import { notificationStore } from 'global/NotificationStore';

import errors from './translations.json';

/**
 * A server error item can arrive in multiple GraphQL/API shapes in this project.
 *
 * @typedef {Object} ServerErrorWithErrorsArray
 * @property {Array<string>} errors - Validation error codes.
 * @property {string} [toStationId] - Optional destination station ID.
 * @property {string} [fromStationId] - Optional source station ID.
 * @property {string} [__typename] - Optional GraphQL typename.
 */

/**
 * @typedef {Object} ServerErrorWithCode
 * @property {string} code - Single validation error code.
 */

/**
 * @typedef {Object} ServerErrorWithErrorsString
 * @property {string} errors - Single validation error code represented as a string.
 */

/**
 * Any supported single server error entry.
 *
 * @typedef {string|ServerErrorWithErrorsArray|ServerErrorWithCode|ServerErrorWithErrorsString} ServerErrorEntry
 */

const DEFAULT_ERROR_LABEL = 'An error occurred. Please reload the page to try again.';
const ERROR_HEADER = 'Error occurred';

const createNotification = (label) => ({ header: ERROR_HEADER, label, appearance: 'danger' });

const getErrorCodesFromItem = (item) => {
	if (typeof item === 'string') return [item];

	if (!item || typeof item !== 'object') return [];

	const codes = [];

	if (typeof item.code === 'string') {
		codes.push(item.code);
	}

	if (typeof item.errors === 'string') {
		codes.push(item.errors);
	}

	if (Array.isArray(item.errors)) {
		codes.push(...item.errors.filter((errorCode) => typeof errorCode === 'string'));
	}

	return codes;
};

const getNotificationFromServerErrors = (data) => {
	const allErrorCodes = data.flatMap(getErrorCodesFromItem);
	const uniqueErrorCodes = [...new Set(allErrorCodes)];
	const translatedErrors = uniqueErrorCodes.map((errorCode) => errors[errorCode]).filter(Boolean);

	if (!translatedErrors.length) return createNotification(DEFAULT_ERROR_LABEL);

	return createNotification(translatedErrors.join(' '));
};

const getNotificationFromThrownError = (error) => {
	const message = error instanceof Error ? error.message?.trim() : '';

	if (!message) return createNotification(DEFAULT_ERROR_LABEL);

	return createNotification(message);
};

/**
 * Builds and pushes an error notification from either server validation errors or thrown errors.
 *
 * Supported error payloads:
 * 1) Array of objects with errors array: [{ errors: ['someCode'] }]
 * 2) Array of objects with code: [{ code: 'someCode' }]
 * 3) Array of objects with errors string: [{ errors: 'someCode' }]
 * 4) Array of raw codes: ['someCode']
 * 5) Thrown runtime errors: new Error('Network request failed')
 *
 * Normalization rules:
 * - Collect all codes from the provided array
 * - Remove duplicate codes
 * - Translate codes using translations.json
 * - Join translated messages into one notification label
 * - Fall back to DEFAULT_ERROR_LABEL when no mapped translation is found
 *
 * @param {Array<ServerErrorEntry>|Error|unknown} error - A server error array or thrown error.
 * @returns {{ header: string, label: string, appearance: string }} notification object that was sent to the store.
 *
 * @example
 * // Shape 1: GraphQL validation array
 * handleError([
 *     {
 *       toStationId: '3e34b42b-c4e7-4523-b8c7-ae9f48e099be',
 *       fromStationId: '99a9d098-e156-482d-95ee-cc6fcd71cab6',
 *       errors: ['tooManyAssignmentsForActivityType'],
 *       __typename: 'WorkAssignmentBatchMutationError',
 *     },
 * ]);
 *
 * @example
 * // Shape 2: code objects
 * handleError([{ code: 'tooManyAssignmentsForActivityType' }]);
 *
 * @example
 * // Shape 3: errors string
 * handleError([{ errors: 'tooManyAssignmentsForActivityType' }]);
 *
 * @example
 * // Shape 4: raw string codes
 * handleError(['tooManyAssignmentsForActivityType']);
 *
 * @example
 * // Shape 5: thrown runtime error
 * handleError(new Error('Network request failed'));
 */
export const handleError = (error) => {
	const notification = Array.isArray(error)
		? getNotificationFromServerErrors(error)
		: getNotificationFromThrownError(error);

	notificationStore.setNotification(notification);

	return notification;
};
